import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import type { NativeLanguage } from "@/types";
import { cn } from "@/lib/utils";

interface WelcomeStepProps {
  displayName: string | null;
  photoURL: string | null;
  selectedLanguage: NativeLanguage;
  onSelectLanguage: (lang: NativeLanguage) => void;
  onContinue: () => void;
}

// TODO: Russian support hidden — re-enable by adding { value: "ru", label: "Русский", flag: "🇷🇺" }
const LANGUAGES: { value: NativeLanguage; label: string; flag: string }[] = [
  { value: "en", label: "English", flag: "🇺🇸" },
];

export function WelcomeStep({
  displayName,
  photoURL,
  selectedLanguage,
  onSelectLanguage,
  onContinue,
}: WelcomeStepProps) {
  const firstName = displayName?.split(" ")[0] || "there";
  const [imgFailed, setImgFailed] = useState(false);

  return (
    <div className="flex flex-col items-center text-center gap-6">
      {photoURL && !imgFailed ? (
        <motion.img
          src={photoURL}
          alt=""
          className="w-20 h-20 rounded-full border-4 border-blue-100 dark:border-blue-900"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
          onError={() => setImgFailed(true)}
          referrerPolicy="no-referrer"
        />
      ) : (
        <motion.div
          className="w-20 h-20 rounded-full border-4 border-blue-100 dark:border-blue-900 bg-blue-50 dark:bg-blue-950 flex items-center justify-center text-3xl font-bold text-blue-500"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
        >
          {firstName[0]?.toUpperCase()}
        </motion.div>
      )}

      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Welcome, {firstName}!
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Let's personalize your Hebrew learning experience
        </p>
      </div>

      <div className="w-full max-w-xs space-y-2">
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
          What's your native language?
        </p>
        <div className="grid grid-cols-2 gap-3">
          {LANGUAGES.map((lang) => (
            <motion.button
              key={lang.value}
              whileTap={{ scale: 0.98 }}
              onClick={() => onSelectLanguage(lang.value)}
              className={cn(
                "flex items-center justify-center gap-2 px-4 py-3 rounded-xl border-2 text-sm font-medium transition-colors",
                selectedLanguage === lang.value
                  ? "border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-400"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-gray-600"
              )}
            >
              <span className="text-lg">{lang.flag}</span>
              {lang.label}
            </motion.button>
          ))}
        </div>
      </div>

      <Button onClick={onContinue} className="w-full max-w-xs" size="lg">
        Continue
      </Button>
    </div>
  );
}
